import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Caption2,
  Subtitle2,
  CompoundButton,
  FluentProvider,
  Button,
  Text,
  MessageBar, MessageBarBody, MessageBarTitle, Spinner,
  MessageBarActions,
  Tooltip,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  tokens,
  ProgressBar,
} from '@fluentui/react-components';
import {
  Play24Regular,
  DocumentTable24Regular, ErrorCircle24Regular, CheckmarkCircle24Regular,
  DocumentText32Regular,
  DocumentData32Regular,
  DocumentSparkle32Regular,
  PlugConnected24Filled,
  Home24Regular
} from '@fluentui/react-icons';
import { useAppStyles, lightTheme } from './styles/appStyles';
import { config, validateConfig, logConfiguration } from '../config/environment';
import './taskpane.css';
import Header from "./Header";
import Footer from "./Footer";
import SubscriptionPage from "./pages/SubscriptionPage";
import ActivationPage from "./pages/subsription/ActivationPage";
import UnregisterPage from "./pages/subsription/UnregisterPage";
import AddCompanyPage from "./pages/bdoentities/AddCompanyPage";
import BDOEntitiesPage from "./pages/BDOEntitiesPage";
import InfoPage from "./pages/help/InfoPage";
import ContactForm from "./pages/help/ContactForm";
import GuidePage from "./pages/GuidePage";
import ProgressPage from "./pages/ProgressPage";

import SummaryReportsCardKPO from "./pages/reports/SummaryReportsCardKPO";
import ReportsControlCardKPO from "./pages/reports/ReportsControlCardKPO";
import ReportsControlCardKEO from "./pages/reports/ReportsControlCardKEO";
import CardKPO from "./pages/reports/PrintCardKPO";
import MonthlyStatementByWasteCode from "./pages/reports/MonthlyStatementByWasteCode";
import AnnualReportWithPermit from "./pages/reports/AnnualReportWithPermit";
import CardKEO from "./pages/reports/PrintCardKEO";

import { DeviceProvider, useDevice } from '../model/DeviceContext';
import { checkHealthEndpoint } from '../model/SubscriptionService';
import CompanySelector from './pages/CompanySelector';

import { useWasteProcessData } from './hooks/useWasteProcessData';
import { useWasteCodeData } from './hooks/useWasteCodeData';
import { useTemplateLoader } from './hooks/useTemplateLoader';
import { initializeGlobalApiFunctions } from '../services/GlobalApiBridge';

interface AppProps {
  title?: string;
}

interface WindowExtended extends Window {
  hideHtmlSplashScreen?: () => void;
}

type PageType = 'home' | 'subscription' | 'activation' | 'unregister' | 'add-company' | 'bdo-entities' | 'help' | 'contact' |
  'summary-reports-kpo' | 'reports-control-kpo' | 'reports-control-keo' | 'reports-cart-kpo' | 'reports-cart-keo' |
  'monthly-statement-waste-code' | 'annual-report-permit' | 'collection-point-add';

/**
 * Main App Content Component
 * Separated to use DeviceContext hook
 */
const AppContent: React.FC<AppProps> = ({ title = "CARBOwaste BDO" }) => {
  const { t, ready: i18nReady } = useTranslation();
  const { userInfo } = useDevice();
  const [isOfficeReady, setIsOfficeReady] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [showNetworkErrorDialog, setShowNetworkErrorDialog] = useState(false);
  const [networkErrorMessage, setNetworkErrorMessage] = useState<string>('');
  const [isRetrying, setIsRetrying] = useState(false);

  // Initialize selectedCompanyId from localStorage, but it will be validated later
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('carbowaste_selected_company_id');
    } catch (error) {
      console.warn('Failed to read selected company from localStorage:', error);
      return null;
    }
  });

  // Validate and auto-fix selectedCompanyId when subscription changes
  // Using subscriptionId as dependency to ensure this runs when subscription data changes
  useEffect(() => {
    console.log('🔍 App useEffect auto-fix running:', {
      hasSubscription: !!subscription,
      hasCompanies: !!subscription?.companies,
      companiesCount: subscription?.companies?.length || 0,
      subscriptionId: subscription?.subscriptionId,
      selectedCompanyId: selectedCompanyId,
      companies: subscription?.companies?.map(c => ({ id: c.companyId, name: c.name }))
    });

    if (subscription?.companies && subscription.companies.length > 0) {
      const companyExists = selectedCompanyId && subscription.companies.some(c => c.companyId === selectedCompanyId);

      console.log('🔍 App: Checking if company exists:', {
        selectedCompanyId,
        companyExists,
        companiesInSubscription: subscription.companies.map(c => c.companyId)
      });

      // Auto-select first company if selectedCompanyId is null or doesn't exist in subscription
      if (!companyExists) {
        const firstCompany = subscription.companies[0];
        console.log('⚠️ App: selectedCompanyId invalid or missing, auto-selecting first company:', {
          oldId: selectedCompanyId,
          newId: firstCompany.companyId,
          companyName: firstCompany.name,
          subscriptionId: subscription.subscriptionId
        });
        setSelectedCompanyId(firstCompany.companyId);
        try {
          localStorage.setItem('carbowaste_selected_company_id', firstCompany.companyId);
        } catch (error) {
          console.warn('Failed to save selected company to localStorage:', error);
        }
      }
    }
  }, [subscription?.subscriptionId, subscription?.companies, selectedCompanyId]);

  // Listen for subscription changes and reset selectedCompanyId
  useEffect(() => {
    const handleSubscriptionChange = (event: CustomEvent) => {
      console.log('🔔 App: Received subscription-change event, resetting selectedCompanyId');
      setSelectedCompanyId(null);
    };

    window.addEventListener('carbowaste:subscription-change', handleSubscriptionChange as EventListener);

    return () => {
      window.removeEventListener('carbowaste:subscription-change', handleSubscriptionChange as EventListener);
    };
  }, []);

  // Wrapper for setSelectedCompanyId that also saves to localStorage
  const handleCompanyChange = useCallback((companyId: string | null) => {
    console.log('🏢 App handleCompanyChange called:', {
      newCompanyId: companyId,
      subscriptionId: subscription?.subscriptionId,
      companiesInSubscription: subscription?.companies?.map(c => ({
        companyId: c.companyId,
        name: c.name,
        eupsCount: c.eups?.length || 0
      }))
    });
    setSelectedCompanyId(companyId);
    try {
      if (companyId) {
        localStorage.setItem('carbowaste_selected_company_id', companyId);
      } else {
        localStorage.removeItem('carbowaste_selected_company_id');
      }
    } catch (error) {
      console.warn('Failed to save selected company to localStorage:', error);
    }
  }, [subscription?.subscriptionId, subscription?.companies]);

  const [previousPage, setPreviousPage] = useState<PageType>('home'); // Track page before guide
  const [showGuide, setShowGuide] = useState<boolean>(() => {
    // Check if user has seen the guide before
    try {
      const hasSeenGuide = localStorage.getItem('carbowaste-firstrun-completed');
      return hasSeenGuide !== 'true'; // Show guide if not completed before
    } catch (error) {
      console.warn('Failed to read firstrun status from localStorage:', error);
      return true; // Show guide by default if localStorage fails
    }
  });
  // Track which accordion items are open to preserve state after navigation
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([]);
  const styles = useAppStyles();

  // Device Context
  const {
    fingerprint,
    subscription,
    isLoading: deviceLoading,
    error: deviceError,
    isRegistered,
    isTrial,
    refreshSubscription,
    addCompanyToTrialSubscription
  } = useDevice();

  // Use 'home' as currentPath since we're not using React Router
  const {
    showProgress: showWasteProcessProgress,
    fetchDataFunction: fetchWasteProcessFunction,
    fetchWasteProcessData,
    handleProgressComplete: handleWasteProcessComplete
  } = useWasteProcessData();

  const {
    showProgress: showWasteCodeProgress,
    fetchDataFunction: fetchWasteCodeFunction,
    fetchWasteCodeData,
    handleProgressComplete: handleWasteCodeComplete
  } = useWasteCodeData();

  // Template loader hook - loads template on app startup
  const {
    isLoading: templateLoading,
    error: templateError,
    isLoaded: templateLoaded,
    loadTemplate
  } = useTemplateLoader();

  // Helper function to render HTML content safely
  const renderSecondaryContent = (translationKey: string) => {
    const content = t(translationKey);
    // Jeśli zawiera HTML tagi, użyj dangerouslySetInnerHTML
    if (content.includes('<') && content.includes('>')) {
      return <span dangerouslySetInnerHTML={{ __html: content }} />;
    }
    // W przeciwnym razie zwróć zwykły tekst
    return content;
  };

  useEffect(() => {
    // Validate and log configuration on app startup
    try {
      validateConfig();
      logConfiguration();
    } catch (error) {
      console.error('❌ Configuration validation failed:', error);
    }

    // Initialize global API bridge for excel-sheets-generator package
    try {
      initializeGlobalApiFunctions();
    } catch (error) {
      console.error('❌ Failed to initialize global API bridge:', error);
    }

    console.log('🚀 App component mounting...', {
      env: config.env,
      apiBaseUrl: config.api.baseUrl,
      debugMode: config.features.enableDebugMode,
      i18nReady,
      title,
      officeExists: typeof Office !== 'undefined',
      windowExists: typeof window !== 'undefined'
    });

    // Sprawdź czy Office.js jest gotowy - NIE WYWOŁUJ Office.onReady() tutaj,
    // bo już zostało wywołane w taskpane.tsx
    const checkOfficeReady = () => {
      try {
        if (typeof Office !== 'undefined' && Office && Office.context) {
          console.log('✅ Office.js is ready');
          setIsOfficeReady(true);
          return true;
        }
        console.log('⏳ Office.js not ready yet');
        return false;
      } catch (error) {
        console.error('❌ Error checking Office.js readiness:', error);
        return false;
      }
    };

    // Sprawdź czy Office jest gotowy, bez wywoływania Office.onReady()
    if (checkOfficeReady()) {
      // Office już gotowy - ustaw flagę, ale NIE zamykaj splash
      // Splash zostanie zamknięty po załadowaniu DeviceContext
      setTimeout(() => {
        setIsAppReady(true);
        console.log('✅ App ready, waiting for DeviceContext...');
      }, 100);
    } else {
      // Office nie jest jeszcze gotowy - poczekaj chwilę i spróbuj ponownie
      console.log('⏳ Waiting for Office.js to be ready...');
      const officeCheckInterval = setInterval(() => {
        if (checkOfficeReady()) {
          clearInterval(officeCheckInterval);
          setTimeout(() => {
            setIsAppReady(true);
            console.log('✅ App ready, waiting for DeviceContext...');
          }, 100);
        }
      }, 200); // Sprawdź co 200ms

      // Timeout fallback - jeśli Office nie będzie gotowy po 10 sekundach
      setTimeout(() => {
        clearInterval(officeCheckInterval);
        console.warn('⚠️ Office.js not ready after timeout, proceeding anyway');
        setIsAppReady(true);
      }, 10000);
    }

    // Cleanup function
    return () => {
      console.log('🧹 App component unmounting');
    };
  }, []);

  // useEffect: Check for pendingAction from context menu and navigate to appropriate page
  useEffect(() => {
    if (isOfficeReady && typeof Office !== 'undefined' && Office.context) {
      const pendingAction = Office.context.document.settings.get("pendingAction");
      console.log("🔍 App.tsx checking pendingAction:", pendingAction);

      if (pendingAction === "createKpoSheet") {
        console.log('📋 Context menu action detected - navigating to PrintCardKPO page');
        setCurrentPage('reports-cart-kpo');
        // Note: We don't clear the pendingAction here - PrintCardKPO will do that after reading the value
      }
    }
  }, [isOfficeReady]);

  // useEffect: Listen for navigation signals from commands.ts (Office.context.document.settings)
  useEffect(() => {
    console.log('🎯 Navigation useEffect mounted. isOfficeReady:', isOfficeReady);

    if (!isOfficeReady) {
      console.log('⏸️ Office not ready yet, skipping navigation setup');
      return;
    }

    console.log('✅ Office ready, starting navigation polling...');

    // Polling - sprawdź Office settings co 500ms
    const pollInterval = setInterval(() => {
      const pendingNav = Office.context.document.settings.get('pendingNavigation');
      const pendingTimestamp = Office.context.document.settings.get('pendingTimestamp');

      if (pendingNav && pendingTimestamp) {
        const timestamp = parseInt(pendingTimestamp as string, 10);
        const age = Date.now() - timestamp;

        if (age < 5000) {
          console.log('📋 FOUND navigation in Office settings:', pendingNav, 'age:', age, 'ms');
          setCurrentPage(pendingNav as PageType);

          // Wyczyść flagę nawigacji
          Office.context.document.settings.remove('pendingNavigation');
          Office.context.document.settings.remove('pendingTimestamp');
          Office.context.document.settings.saveAsync((result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log('✅ Navigation applied, flags cleared');
            }
          });
        } else if (age >= 5000) {
          console.log('🗑️ Clearing old Office settings signal (> 5s)');
          Office.context.document.settings.remove('pendingNavigation');
          Office.context.document.settings.remove('pendingTimestamp');
          Office.context.document.settings.saveAsync();
        }
      }
    }, 500);

    console.log('✅ Polling started');

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up navigation polling');
      clearInterval(pollInterval);
    };
  }, [isOfficeReady]);

  // useEffect: Monitor currentPage changes
  useEffect(() => {
    console.log('📄 App: currentPage changed to:', currentPage);
    console.log('🏢 App: Current selectedCompanyId:', selectedCompanyId);
    console.log('🏢 App: subscription companies:', subscription?.companies?.map(c => ({
      id: c.companyId,
      name: c.name,
      eupsCount: c.eups?.length || 0
    })));
  }, [currentPage, selectedCompanyId, subscription?.companies]);

  // useEffect: Zamknij HTML splash gdy Office, App, i18n i DeviceContext są gotowe
  // KRYTYCZNE: Splash może być zamknięty dopiero po zakończeniu fetchDeviceSubscription I ładowania szablonu
  useEffect(() => {
    // Sprawdź czy wszystkie komponenty są gotowe: Office, App, i18n i DeviceContext
    if (isOfficeReady && isAppReady && i18nReady) {
      console.log('✅ Core systems ready (Office, App, i18n)');

      // CZEKAJ na zakończenie DeviceContext (fetchDeviceSubscription musi się zakończyć)
      if (deviceLoading) {
        console.log('⏳ Waiting for DeviceContext to finish (fetchDeviceSubscription in progress)...');
        console.log('⏳ htmlSplashScreen will remain visible until subscription data is loaded');
        return; // Nie zamykaj splash jeszcze - czekaj na koniec fetchDeviceSubscription
      }

      console.log('✅ DeviceContext ready - fetchDeviceSubscription completed');
      console.log('✅ Subscription data loaded:', subscription ? `${subscription.status}` : 'null');

      // Jeśli był błąd podczas inicjalizacji urządzenia, zapisz go
      if (deviceError) {
        console.warn('⚠️ Device initialization error detected:', deviceError);
        setNetworkErrorMessage(deviceError);
        setShowNetworkErrorDialog(true);
      }

      // CZEKAJ na zakończenie ładowania szablonu (jeśli jest w toku)
      if (templateLoading) {
        console.log('⏳ Waiting for template to load...');
        console.log('⏳ htmlSplashScreen will remain visible until template is loaded');
        return; // Nie zamykaj splash jeszcze - czekaj na koniec ładowania szablonu
      }

      // Szablon startowy ładujemy TYLKO dla Trial mode
      if (isTrial) {
        // Sprawdź czy szablon był już ładowany lub czy jest błąd
        const wasTemplateLoaded = sessionStorage.getItem('carbowaste_template_loaded_session') === 'true';

        if (!wasTemplateLoaded && !templateLoaded && !templateError) {
          console.log('⏳ Trial mode: Template not loaded yet, initiating load...');

          // Załaduj szablon startowy z API
          loadTemplate()
            .then(() => {
              console.log('✅ Template loaded successfully, now closing splash screen');
              notifyHtmlSplashScreen();
            })
            .catch((error) => {
              console.warn('⚠️ Template loading failed, but continuing:', error);
              // Nawet jeśli szablon się nie załaduje, ukryj splash screen
              notifyHtmlSplashScreen();
            });
          return; // Czekaj na zakończenie loadTemplate
        }
      } else {
        console.log('✅ Registered subscription - skipping template load');
      }

      // Wszystko gotowe - szablon już załadowany lub wystąpił błąd
      console.log('✅ All systems ready (template:', templateLoaded ? 'loaded' : (templateError ? 'error' : 'skipped'), '), closing HTML splash...');
      notifyHtmlSplashScreen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfficeReady, isAppReady, i18nReady, deviceLoading, deviceError, templateLoading, templateLoaded, templateError]); // Dodano template states

  // Handle guide completion
  const handleGuideComplete = async () => {
    console.log('Guide completed - returning to previous page:', previousPage);
    setShowGuide(false);
    setCurrentPage(previousPage); // Return to page user was on before opening guide
    try {
      localStorage.setItem('carbowaste-firstrun-completed', 'true');
    } catch (error) {
      console.warn('Failed to save firstrun completion status:', error);
    }

    // Załaduj szablon startowy po zakończeniu przewodnika - TYLKO dla Trial mode
    if (isTrial) {
      try {
        console.log('🔄 Trial mode: Ponowne ładowanie szablonu startowego po zakończeniu przewodnika...');

        // Wyczyść flagę sessionStorage, aby wymusić ponowne pobranie z API
        sessionStorage.removeItem('carbowaste_template_loaded_session');
        console.log('🗑️ Wyczyszczono flagę szablonu z sessionStorage');

        await loadTemplate();
        console.log('✅ Szablon startowy załadowany pomyślnie');
      } catch (error) {
        console.error('❌ Błąd podczas ładowania szablonu startowego:', error);
        // Nie przerywaj działania aplikacji - szablon jest opcjonalny
      }
    } else {
      console.log('✅ Registered subscription - skipping template reload after guide');
    }
  };

  // Handle guide disable (don't show again)
  const handleGuideDisable = async () => {
    console.log('Guide disabled by user - will not show on next startup');
    setShowGuide(false);
    setCurrentPage(previousPage); // Return to page user was on before opening guide
    try {
      localStorage.setItem('carbowaste-firstrun-completed', 'true');
      localStorage.setItem('carbowaste-firstrun-dont-show-again', 'true');
    } catch (error) {
      console.warn('Failed to save firstrun disable status:', error);
    }
  };

  // Funkcja powiadamiająca HTML splash screen o gotowości React app
  const notifyHtmlSplashScreen = () => {
    console.log('📢 Notifying HTML splash screen that React app is ready');

    try {
      const extendedWindow = window as WindowExtended;
      if (typeof extendedWindow.hideHtmlSplashScreen === 'function') {
        extendedWindow.hideHtmlSplashScreen();
        console.log('✅ Successfully called hideHtmlSplashScreen()');
      } else {
        console.warn('⚠️ hideHtmlSplashScreen function not found on window object');
      }
    } catch (error) {
      console.error('❌ Error calling hideHtmlSplashScreen:', error);
    }
  };

  // Renderuj tylko błąd krytyczny jeśli Office, App lub i18n nie jest gotowy
  // (te komponenty są wymagane do działania aplikacji)
  // deviceLoading NIE blokuje renderowania - błąd sieci pokazuje się przez dialog
  if (!isOfficeReady || !isAppReady || !i18nReady) {
    // Ten stan powinien być obsłużony przez HTML SplashScreen
    // Jeśli się pojawia, to oznacza problem z inicjalizacją
    return (
      <div className="app-container loading">
        <div className="loading-content">
          <h2>Błąd inicjalizacji aplikacji</h2>
          <p>Nie udało się załadować niezbędnych komponentów.</p>
        </div>
      </div>
    );
  }

  // Show GuidePage (First Run Experience) after splash but before main app
  if (showGuide) {
    // Determine variant: 'startup' only on first app load (previousPage is 'home' and not set manually)
    // Otherwise 'info' when opened from menu
    const isStartup = previousPage === 'home' && !localStorage.getItem('carbowaste-firstrun-completed');

    return (
      <FluentProvider theme={lightTheme}>
        <GuidePage
          onComplete={handleGuideComplete}
          onDisable={handleGuideDisable}
          variant={isStartup ? "startup" : "info"}
          showHeader={false}
        />
      </FluentProvider>
    );
  }

  // Check if subscription is inactive and should block access
  // Only block if status is Suspended/Cancelled AND isTrial is false
  const shouldBlockAccess = subscription &&
    (subscription.status === 'Suspended' || subscription.status === 'Cancelled') &&
    !isTrial;

  if (shouldBlockAccess) {
    return (
      <FluentProvider theme={lightTheme}>
        <div className={styles.root}>
          <Header message={null} onNavigate={() => { }} />
          <div className={styles.app_container}>
            <main className={styles.container}>
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>
                    {t('message_trial_expired_title', 'Brak aktywnej subskrypcji')}
                  </MessageBarTitle>
                  {t('message_trial_expired_body', 'Twoja subskrypcja wygasła lub została zawieszona.')}
                  <br />
                  <br />
                  <strong>Status:</strong> {subscription.status}
                  <br />
                  <strong>Plan:</strong> {subscription.planId}
                  <br />
                  <strong>Data zakończenia:</strong> {new Date(subscription.endDate).toLocaleDateString('pl-PL')}
                </MessageBarBody>
              </MessageBar>
            </main>
          </div>
        </div>
      </FluentProvider>
    );
  }

  // Main application interface - always accessible (registered, trial, or with error)
  // Generate header message based on subscription status
  const getHeaderMessage = () => {
    if (!subscription) return null;

    // Suspended status
    if (subscription.status === 'Suspended') {
      return {
        text: isTrial
          ? t('device.subscription_suspended_trial', 'Subskrypcja zawieszona - dostęp w trybie testowym')
          : t('device.subscription_suspended', 'Subskrypcja zawieszona - ograniczona funkcjonalność'),
        intent: "error" as const
      };
    }

    // Cancelled status
    if (subscription.status === 'Cancelled') {
      return {
        text: isTrial
          ? t('device.subscription_cancelled_trial', 'Subskrypcja anulowana - dostęp w trybie testowym')
          : t('device.subscription_cancelled', 'Subskrypcja anulowana - ograniczona funkcjonalność'),
        intent: "error" as const
      };
    }

    // Trial status (normal trial, not suspended/cancelled)
    if (isTrial) {
      return {
        text: (
          <>
            {t('device.trial_simple', 'Aplikacja w wersji próbnej')}
            {' - '}
            <a
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage('subscription');
              }}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '2px'
              }}
            >
              sprawdź subskrypcję
              <span style={{ fontSize: '12px' }}>→</span>
            </a>
          </>
        ),
        intent: "warning" as const
      };
    }

    return null;
  };

  const headerMessage = getHeaderMessage();

  const handleNavigation = (page: string) => {
    if (page === 'subscription') {
      setCurrentPage('subscription');
    } else if (page === 'activation') {
      setCurrentPage('activation');
    } else if (page === 'unregister') {
      setCurrentPage('unregister');
    } else if (page === 'bdo-entities') {
      setCurrentPage('bdo-entities');
    } else if (page === 'help') {
      setCurrentPage('help');
    } else if (page === 'contact') {
      setCurrentPage('contact');
    } else if (page === 'guide') {
      // Save current page before showing guide
      setPreviousPage(currentPage);
      setShowGuide(true);
      console.log('Opening guide from menu, previous page:', currentPage);
    } else {
      setCurrentPage('home');
    }
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
  };

  const handleAddCompanyFromMessage = () => {
    console.log('Add company clicked from message bar - navigating to Add Company page');
    setCurrentPage('add-company');
  };

  const handleRetryConnection = async () => {
    console.log('🔄 Retrying network connection...');
    setIsRetrying(true);
    setShowNetworkErrorDialog(false);

    try {
      // First check if API is available using health endpoint
      console.log('📡 Step 1: Checking API health...');
      const isApiHealthy = await checkHealthEndpoint();

      if (!isApiHealthy) {
        throw new Error('API health check failed - server is not responding');
      }

      console.log('✅ API is healthy, refreshing subscription...');
      // If health check passes, refresh subscription
      await refreshSubscription();
      console.log('✅ Retry successful');
    } catch (error) {
      console.error('❌ Retry failed:', error);
      setNetworkErrorMessage(error instanceof Error ? error.message : 'Unknown error');
      setShowNetworkErrorDialog(true);
    } finally {
      setIsRetrying(false);
    }
  };

  const hasCollectionPoints = (subscription?.companies?.length ?? 0) > 0;

  return (
    <FluentProvider theme={lightTheme}>
      <div className={styles.root}>
        {/* Header sekcja z logo */}
        <Header message={headerMessage} onNavigate={handleNavigation} userInfo={userInfo} />
        <div className={styles.app_container}>

          {/* Dialog z błędem sieciowym */}
          <Dialog
            open={showNetworkErrorDialog}
            onOpenChange={(event, data) => {
              // Zapobiegaj zamknięciu przez ESC lub kliknięcie poza dialog
              // Dialog można zamknąć tylko przez przyciski
              if (data.open === false) {
                event?.preventDefault();
              }
            }}
          >
            <DialogSurface className={styles.errorDialogSurface}>
              <DialogBody>
                <DialogTitle>
                  <ErrorCircle24Regular style={{ marginRight: '8px', color: 'var(--colorPaletteRedForeground1)' }} />
                  <span className={styles.errorDialogTitle}>
                    {t('device.connection_error_title', 'Błąd połączenia')}
                  </span>
                </DialogTitle>
                <DialogContent>
                  <Text>
                    {t('device.connection_error_message', 'Wystąpił błąd połączenia sieciowego z usługami.')}
                  </Text>
                  <br />
                  <br />
                  <Text>
                    {t('device.trial_mode_fallback', 'Aplikacja będzie działać w wersji próbnej.')}
                  </Text>
                </DialogContent>
                <DialogActions>
                  <Button
                    appearance="primary"
                    disabled={isRetrying}
                    onClick={handleRetryConnection}
                  >
                    {isRetrying ? (
                      <>
                        <Spinner size="tiny" style={{ marginRight: '8px' }} />
                        {t('device.retrying_connection', 'Wznawianie...')}
                      </>
                    ) : (
                      t('device.retry_connection', 'Spróbuj ponownie')
                    )}
                  </Button>
                  <Button
                    appearance="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowNetworkErrorDialog(false);
                    }}
                  >
                    {t('common.close', 'Zamknij')}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>

          {/* Overlay dla retry connection */}
          {isRetrying && (
            <div className={styles.retryOverlay}>
              <div className={styles.retryContainer}>
                <PlugConnected24Filled
                  className={styles.retryIcon}
                />
                <ProgressBar
                  thickness="medium"
                  style={{ width: '200px', marginTop: '8px' }}
                />
                <Text size={300} weight="regular" style={{ color: tokens.colorNeutralForeground2, marginTop: '8px' }}>
                  {t('device.retrying_connection_title', 'Wznawianie połączenia sieciowego...')}
                </Text>
              </div>
            </div>
          )}

          {/* Progress dla pobierania kodów odpadów */}
          {showWasteCodeProgress && fetchWasteCodeFunction && (
            <ProgressPage
              fetchData={fetchWasteCodeFunction}
              loadingMessage={t('progress_fetching_waste_codes', 'Pobieranie słownika kodów odpadów...')}
              successMessage={t('progress_waste_codes_success', 'Słownik kodów odpadów został pobrany')}
              backTo="/"
              onComplete={handleWasteCodeComplete}
              reportCode="SL01"
              reportName="Słownik kodów odpadów"
              additionalInfo="Pobieranie danych może potrwać dłuższą chwilę..."
            />
          )}

          {/* Progress dla pobierania procesów */}
          {showWasteProcessProgress && fetchWasteProcessFunction && (
            <ProgressPage
              fetchData={fetchWasteProcessFunction}
              loadingMessage={t('progress_fetching_waste_processes', 'Pobieranie słownika procesów odzysku...')}
              successMessage={t('progress_waste_processes_success', 'Słownik procesów odzysku został pobrany')}
              backTo="/"
              onComplete={handleWasteProcessComplete}
              reportCode="SL02"
              reportName="Słownik procesów odzysku"
              additionalInfo="Pobieranie danych może potrwać dłuższą chwilę..."
            />
          )}

          {currentPage === 'subscription' && (
            <SubscriptionPage onBack={handleBackToHome} onNavigate={handleNavigation} />
          )}

          {currentPage === 'activation' && (
            <ActivationPage
              onBack={() => setCurrentPage('subscription')}
              onSuccess={() => {
                setCurrentPage('subscription');
              }}
            />
          )}

          {currentPage === 'unregister' && (
            <UnregisterPage
              onBack={() => setCurrentPage('subscription')}
              onSuccess={() => {
                setCurrentPage('subscription');
              }}
            />
          )}

          {currentPage === 'add-company' && (
            <AddCompanyPage
              onBack={handleBackToHome}
              onSuccess={() => {
                console.log('✅ Company added successfully, returning to home');
                setCurrentPage('home');
              }}
            />
          )}

          {currentPage === 'help' && (
            <InfoPage onBack={handleBackToHome} />
          )}

          {currentPage === 'contact' && (
            <ContactForm onBack={handleBackToHome} />
          )}

          {currentPage === 'bdo-entities' && (
            <BDOEntitiesPage onBack={handleBackToHome} />
          )}

          {currentPage === 'summary-reports-kpo' && (
            <SummaryReportsCardKPO
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}

          {currentPage === 'reports-control-kpo' && (
            <ReportsControlCardKPO
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}
          {currentPage === 'reports-control-keo' && (
            <ReportsControlCardKEO
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}
          {currentPage === 'reports-cart-kpo' && (
            <CardKPO
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}

          {currentPage === 'reports-cart-keo' && (

            <CardKEO
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />

          )}

          {currentPage === 'monthly-statement-waste-code' && (
            <MonthlyStatementByWasteCode
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}

          {currentPage === 'annual-report-permit' && (
            <AnnualReportWithPermit
              onBack={handleBackToHome}
              companyId={selectedCompanyId}
            />
          )}

          {currentPage === 'home' && (
            <main className={styles.appPageContainer}>
              <div className={styles.appPageHeader}>
                <Home24Regular className={styles.appPageHeaderIcon} />
                <Text className={styles.appPageTitle}>
                  {t("AccordionHeader_header", "Raporty i zestawienia danych BDO")}
                </Text>
              </div>
              {!hasCollectionPoints && (
                <MessageBar intent="warning" style={{ marginBottom: 16 }}>
                  <MessageBarBody>
                    <MessageBarTitle>{t("message_no_default_point_title")}</MessageBarTitle>
                    {t("message_no_default_point_body")}
                  </MessageBarBody>
                  <MessageBarActions>
                    <Tooltip withArrow
                      content={{ children: t("message_no_default_point_button_tooltip") }}
                      relationship="label"
                    >
                      <Button
                        appearance="primary"
                        onClick={handleAddCompanyFromMessage}
                      >
                        {t("message_no_default_point_button")}
                      </Button>
                    </Tooltip>
                  </MessageBarActions>
                </MessageBar>
              )}

              {/* Komponent do wyboru firmy - widoczny tylko gdy hasCollectionPoints is true */}
              {hasCollectionPoints && (
                <CompanySelector
                  selectedCompanyId={selectedCompanyId}
                  onCompanyChange={handleCompanyChange}
                />
              )}

              {/* Komponent Accordion z opcjami raportów */}
              <Accordion
                openItems={openAccordionItems}
                onToggle={(event, data) => setOpenAccordionItems(data.openItems as string[])}
                collapsible
              >
                {/* Opcje raportów */}
                {/* Raporty kontrolne KPO i KEO */}
                <AccordionItem value="1" disabled={!hasCollectionPoints || !!deviceError}>
                  <AccordionHeader>
                    <div className={styles.accordionheader}>
                      <Subtitle2>{t("AccordionHeader_control_reports")}</Subtitle2>
                      <Caption2>{t("AccordionHeader_control_reports_description")}</Caption2>
                    </div>
                  </AccordionHeader>
                  <AccordionPanel>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        icon={<DocumentData32Regular />}
                        size="small"
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_control_reports_option3_subtext")}
                        onClick={() => setCurrentPage('summary-reports-kpo')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW01</span> <span className={styles.reportTitleText}>{t("AccordionHeader_control_reports_option3")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        icon={<DocumentData32Regular />}
                        size="small"
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_control_reports_option1_subtext")}
                        onClick={() => setCurrentPage('reports-control-kpo')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW02</span> <span className={styles.reportTitleText}>{t("AccordionHeader_control_reports_option1")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        icon={<DocumentData32Regular />}
                        size="small"
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_control_reports_option2_subtext")}
                        onClick={() => setCurrentPage('reports-control-keo')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW03</span> <span className={styles.reportTitleText}>{t("AccordionHeader_control_reports_option2")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        icon={<DocumentData32Regular />}
                        size="small"
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_control_reports_option4_subtext")}
                        onClick={() => setCurrentPage('reports-cart-kpo')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW04</span> <span className={styles.reportTitleText}>{t("AccordionHeader_control_reports_option4")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        icon={<DocumentData32Regular />}
                        size="small"
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_control_reports_option5_subtext")}
                        onClick={() => setCurrentPage('reports-cart-keo')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW05</span> <span className={styles.reportTitleText}>{t("AccordionHeader_control_reports_option5")}</span></span>
                      </CompoundButton>
                    </div>
                  </AccordionPanel>
                </AccordionItem>
                {/* Zestawienie wytworzonych odpadów */}
                <AccordionItem value="2" disabled={!hasCollectionPoints || !!deviceError}>
                  <AccordionHeader>
                    <div className={styles.accordionheader}>
                      <Subtitle2>{t("AccordionHeader_generated_reports")}</Subtitle2>
                      <Caption2>{t("AccordionHeader_generated_reports_description")}</Caption2>
                    </div>
                  </AccordionHeader>
                  <AccordionPanel>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        size="small"
                        icon={<DocumentSparkle32Regular />}
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_generated_reports_option1_subtext")}
                        onClick={() => setCurrentPage('monthly-statement-waste-code')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW11</span> <span className={styles.reportTitleText}>{t("AccordionHeader_generated_reports_option1")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        size="small"
                        icon={<DocumentSparkle32Regular />}
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_generated_reports_option2_subtext")}
                        onClick={() => setCurrentPage('annual-report-permit')}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>CW12</span> <span className={styles.reportTitleText}>{t("AccordionHeader_generated_reports_option2")}</span></span>
                      </CompoundButton>
                    </div>
                  </AccordionPanel>
                </AccordionItem>
                {/* Słowniki systemowe BDO */}
                <AccordionItem value="3" disabled={!!deviceError}>
                  <AccordionHeader>
                    <div className={styles.accordionheader}>
                      <Subtitle2>{t("AccordionHeader_dictionary")}</Subtitle2>
                      <Caption2>{t("AccordionHeader_dictionary_description")}</Caption2>
                    </div>
                  </AccordionHeader>
                  <AccordionPanel>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        size="small"
                        icon={<DocumentText32Regular />}
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_dictionary_option1_subtext")}
                        onClick={fetchWasteCodeData}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>SL01</span> <span className={styles.reportTitleText}>{t("AccordionHeader_dictionary_option1")}</span></span>
                      </CompoundButton>
                    </div>
                    <div>
                      <CompoundButton
                        appearance="subtle"
                        size="small"
                        icon={<DocumentText32Regular />}
                        className={styles.compoundButtonSubtleHover}
                        secondaryContent={renderSecondaryContent("AccordionHeader_dictionary_option2_subtext")}
                        onClick={fetchWasteProcessData}>
                        <span style={{ display: 'flex', alignItems: 'center' }}><span className={styles.reportCodeBadge}>SL02</span> <span className={styles.reportTitleText}>{t("AccordionHeader_dictionary_option2")}</span></span>
                      </CompoundButton>
                    </div>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

            </main>
          )}

          <Footer
            onNavigate={handleNavigation}
            hasNetworkError={!!deviceError}
            onRetryConnection={handleRetryConnection}
          />
        </div>
      </div>
    </FluentProvider>
  );
};

/**
 * App Component with DeviceProvider
 */
const App: React.FC<AppProps> = (props) => {
  return (
    <DeviceProvider>
      <AppContent {...props} />
    </DeviceProvider>
  );
};

export default App;